<template>
  <div class="column">
    <a-modal
      v-model="pdfPreviewVisible"
      :footer="null"
      :destroy-on-close="true"
      width="800px"
      @cancel="closePdfPreview"
    >
      <pdf-viewer v-if="pdfPreviewFile" :file="pdfPreviewFile" />
    </a-modal>

    <h4 v-if="withTitle" class="m-0">Attachments</h4>
    <div>
      <div class="ant-upload-list ant-upload-list-picture-card">
        <div class="ant-upload-list-picture-card-container" v-for="file in fileList" :key="file.uid">
          <div class="ant-upload-list-item ant-upload-list-item-done ant-upload-list-item-list-type-picture-card">
            <div class="ant-upload-list-item-info">
              <a-icon
                v-if="file.status === 'uploading'"
                type="loading"
                class="anticon"
                :style="{
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                }"
              />
              <template v-else>
                <img
                  v-if="isImageUrl(file)"
                  :src="file.thumbUrl || file.url || file.preview"
                  :alt="file.name"
                  class="ant-upload-list-item-image"
                />
                <div v-else class="flex flex-column gap-1 justify-content-center align-items-center h-full w-full">
                  <a-icon
                    :type="getFileTypeIcon(file).icon"
                    theme="twoTone"
                    class="anticon"
                    :style="{ fontSize: '30px' }"
                    :two-tone-color="getFileTypeIcon(file).color"
                  />
                  <p
                    :style="{
                      textAlign: 'center',
                      fontSize: '10px',
                      maxWidth: '100%'
                    }"
                  >
                    {{ getFileName(file) }}
                  </p>
                </div>
              </template>
            </div>
            <span class="ant-upload-list-item-actions">
              <image-preview
                :options="{
                  initialViewIndex: previewImages.findIndex(item => item === getFileSource(file))
                }"
                v-if="isImageUrl(file)"
                :images="previewImages"
              >
                <a-icon type="eye-o"
              /></image-preview>
              <a-icon v-else-if="isPdfUrl(file)" type="eye-o" @click="openPdfPreview(file)" />

              <a-icon type="download" @click="handleDownload(file)" />
              <a-icon v-if="!isView" type="delete" @click="handleRemove(file)" />
            </span>
          </div>
        </div>
        <a-upload
          list-type="picture-card"
          :file-list="fileList"
          :before-upload="beforeUpload"
          :remove="handleRemove"
          :custom-request="customRequestHandler"
          :action="null"
          :showUploadList="false"
          :multiple="true"
          @change="handleChange"
          @download="handleDownload"
          :accept="accept"
          :style="{ width: 'fit-content' }"
        >
          <div v-if="!isView">
            <a-icon type="plus" />
            <div class="ant-upload-text">Upload</div>
          </div>
        </a-upload>
      </div>
    </div>
  </div>
</template>

<script>
import ImageViewer from '@/components/entities/viewer/image-viewer.vue'
import PdfViewer from '@/components/entities/viewer/pdf-viewer.vue'
import { Icon, Modal, notification, Upload } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'

const { mapActions: modalActions } = createNamespacedHelpers('modals')

export default {
  components: {
    'a-upload': Upload,
    'a-icon': Icon,
    'a-modal': Modal,
    'image-preview': ImageViewer,
    'pdf-viewer': PdfViewer
  },
  props: {
    files: {
      type: Array,
      default: () => []
    },
    withTitle: {
      type: Boolean,
      default: true
    },
    isView: {
      type: Boolean,
      default: false
    },
    accept: {
      type: String,
      default: '.mp4,.mov,.mkv,.wmv,.webm,.avi,.flv,.mts,.png,.jpg,.jpeg,.svg,.doc,.docx,.pdf,.xls,.xlsx,.txt'
    },
    deleteConfirmation: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {
      fileList: [],
      uploadList: [],
      pdfPreviewVisible: false,
      pdfPreviewFile: null,

      loading: false
    }
  },
  watch: {
    files: {
      handler(newVal) {
        this.fileList = newVal
          .map(item => ({
            uid: item.id,
            name: item.url,
            url: item.url,
            status: 'done'
          }))
          .sort((a, b) => a.uid - b.uid)
      },
      immediate: true,
      deep: true
    }
  },
  computed: {
    savedFiles() {
      return this.fileList.filter(item => this.isSavedFileId(item.uid))
    },
    previewImages() {
      const urls = this.fileList.map(file => {
        if (!this.getFileSource(file) || !this.isImageUrl(file)) {
          return null
        }

        return this.getFileSource(file)
      })

      return urls.filter(Boolean)
    }
  },
  methods: {
    ...modalActions(['openModal']),

    isSavedFileId(id) {
      return typeof id === 'number' || (typeof id === 'string' && /^\d+$/.test(id))
    },

    getSavedFilePayload() {
      return this.savedFiles.map(item => ({ id: Number(item.uid) }))
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },
    extname(url = '') {
      const temp = url.split('/')
      const filename = temp[temp.length - 1]
      const filenameWithoutSuffix = filename.split(/#|\?/)[0]
      return (/\.[^./\\]*$/.exec(filenameWithoutSuffix) || [''])[0]
    },
    isImageFileType(type) {
      return !!type && type.indexOf('image/') === 0
    },
    isPdfFileType(type) {
      return !!type && type.toLowerCase() === 'application/pdf'
    },
    getFileSource(file) {
      return file.url || file.preview || file.thumbUrl || ''
    },
    isImageUrl(file) {
      if (this.isImageFileType(file.type)) {
        return true
      }
      const url = this.getFileSource(file)
      const extension = this.extname(url)
      if (/^data:image\//.test(url) || /(webp|svg|png|gif|jpg|jpeg|jfif|bmp|dpg|ico)$/i.test(extension)) {
        return true
      }
      if (/^data:/.test(url)) {
        // other file types of base64
        return false
      }
      if (extension) {
        // other file types which have extension
        return false
      }
      return true
    },
    isPdfUrl(file) {
      if (this.isPdfFileType(file.type)) {
        return true
      }

      const url = this.getFileSource(file)
      const extension = this.extname(url)

      return /^data:application\/pdf/.test(url) || /\.pdf$/i.test(extension)
    },
    getFileTypeIcon(file) {
      if (file.type) {
        switch (file.type) {
          case 'application/pdf':
            return { icon: 'file-pdf', color: '#ef4444' }
          case 'application/msword':
          case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            return { icon: 'file-word', color: '#3b82f6' }
          case 'application/vnd.ms-excel':
          case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            return { icon: 'file-excel', color: '#10b981' }
          case 'application/vnd.ms-powerpoint':
          case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
            return { icon: 'file-ppt', color: '#ef4444' }
          case 'application/zip':
          case 'application/x-rar-compressed':
            return { icon: 'file-zip', color: '#999999' }
          case 'video/mp4':
          case 'video/quicktime':
          case 'video/x-msvideo':
          case 'video/x-matroska':
          case 'video/webm':
          case 'video/x-flv':
            return { icon: 'video-camera', color: '#2563eb' }
          default:
            break
        }
      }

      const url = file.thumbUrl || file.url
      const extension = this.extname(url)

      switch (extension) {
        case '.pdf':
          return { icon: 'file-pdf', color: '#ef4444' }
        case '.doc':
        case '.docx':
          return { icon: 'file-word', color: '#3b82f6' }
        case '.xls':
        case '.xlsx':
          return { icon: 'file-excel', color: '#10b981' }
        case '.ppt':
        case '.pptx':
          return { icon: 'file-ppt', color: '#ef4444' }
        case '.zip':
        case '.rar':
          return { icon: 'file-zip', color: '#999999' }
        case '.mp4':
        case '.mov':
        case '.mkv':
        case '.wmv':
        case '.webm':
        case '.avi':
        case '.flv':
        case '.mts':
          return { icon: 'video-camera', color: '#d47aff' }
        default:
          return { icon: 'file', color: '#999999' }
      }
    },

    getFileNameWithoutExtension(url) {
      let fileName = url.substring(url.lastIndexOf('/') + 1)

      if (fileName.includes('.')) {
        fileName = fileName.substring(0, fileName.lastIndexOf('.'))
      }

      return fileName
    },

    getFileName(file) {
      const name = file.url.includes('base64')
        ? this.getFileNameWithoutExtension(file.name)
        : this.getFileNameWithoutExtension(file.name ?? file.thumbUrl ?? file.url)

      const extension = file.url.includes('base64') ? this.extname(file.name) : this.extname(file.thumbUrl ?? file.url)

      const result = name.split('_')

      let processedOriginal
      let processed

      if (result.length < 3 || file.url.includes('base64')) {
        processedOriginal = result.join(' ')
      } else {
        processedOriginal = result.slice(0, result.length - 2).join(' ')
      }

      if (!extension) {
        processed = processedOriginal.slice(0, 45)
      } else {
        const maxNameLength = 45 - extension.length - 1
        processed = processedOriginal.slice(0, maxNameLength > 0 ? maxNameLength : 0)
      }

      const wasTruncated = processed.length < processedOriginal.length
      processed = wasTruncated ? processed + '...' : processed

      return extension ? `${processed}${extension}` : processed
    },

    customRequestHandler(options) {
      options.onSuccess('success', options.file)
    },

    handleRemove(file) {
      if (this.deleteConfirmation) {
        const { title, content } = this.deleteConfirmation

        this.openModal({
          type: 'deletion',
          data: {
            visible: true,
            title,
            description: content,
            onConfirm: async () => {
              this.fileList = this.fileList.filter(item => item.uid !== file.uid)

              this.$emit(
                'save',
                this.getSavedFilePayload(),
                'remove',
                file.uid
              )
            }
          }
        })
      } else {
        this.fileList = this.fileList.filter(item => item.uid !== file.uid)

        this.$emit(
          'save',
          this.getSavedFilePayload(),
          'remove',
          file.uid
        )
      }
    },

    beforeUpload(file, fileList) {
      const allowedExtensions = [
        'mp4',
        'mov',
        'mkv',
        'wmv',
        'webm',
        'avi',
        'flv',
        'mts',
        'png',
        'jpg',
        'jpeg',
        'svg',
        'doc',
        'docx',
        'pdf',
        'xls',
        'xlsx',
        'txt'
      ]

      const fileExtension = file.name.split('.').pop().toLowerCase()

      if (!allowedExtensions.includes(fileExtension)) {
        this.showNotification('error', 'Error', 'This file type is not allowed.')
        return false
      }

      if (fileList.length > 0 && fileList[fileList.length - 1] === file) {
        this.customRequest(fileList)
      }

      return true
    },

    async customRequest(uploadedFiles) {
      this.loading = true
      try {
        const data = await this.$store.dispatch('uploadFiles', {
          files: uploadedFiles
        })
        if (!data || !data.ids || !data.ids.length) {
          throw new Error('Error occurred while uploading files.')
        }

        const processedFiles = await Promise.all(
          uploadedFiles.map(async (item, idx) => {
            const base64Url = await this.getBase64(this.getBlobFromUploadFile(item))

            return {
              uid: data.ids[idx],
              originFileObj: item,
              status: 'done',
              name: item.name,
              size: item.size,
              type: item.type,
              lastModified: item.lastModified,
              lastModifiedDate: item.lastModifiedDate,
              preview: item.preview ?? base64Url,
              url: base64Url
            }
          })
        )

        this.fileList = [
          ...this.fileList.filter(item => !uploadedFiles.some(file => file.uid === item.uid)),
          ...processedFiles
        ]

        this.$emit(
          'save',
          this.fileList
            .sort((a, b) => a.uid - b.uid)
            .filter(item => this.isSavedFileId(item.uid))
            .map(item => ({ id: Number(item.uid) })),
          'add'
        )
      } catch (error) {
        this.fileList = this.fileList.filter(item => !uploadedFiles.some(file => file.uid === item.uid))
        this.showNotification('error', 'Upload Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loading = false
      }
    },

    getBlobFromUploadFile(file) {
      if (file instanceof Blob) {
        return file
      }

      if (file?.originFileObj instanceof Blob) {
        return file.originFileObj
      }

      if (file?.file instanceof Blob) {
        return file.file
      }

      return null
    },

    getBase64(file) {
      return new Promise((resolve, reject) => {
        const blob = file instanceof Blob ? file : this.getBlobFromUploadFile(file)

        if (!blob) {
          reject(new TypeError('File must be a Blob or File'))
          return
        }

        const reader = new FileReader()
        reader.readAsDataURL(blob)
        reader.onload = () => resolve(reader.result)
        reader.onerror = error => reject(error)
      })
    },

    handleChange(info) {
      const { fileList } = info
      this.fileList = fileList.map(file => {
        if (typeof file.uid === 'string') {
          file.status = 'uploading'
        }

        if (!file.url && !file.preview) {
          this.getBase64(this.getBlobFromUploadFile(file))
            .then(preview => {
              file.preview = preview
            })
            .catch(() => {})
        }
        return file
      })
    },

    async handleDownload(file) {
      try {
        let filePreview = file.preview
        if (!file.url && !filePreview) {
          filePreview = await this.getBase64(this.getBlobFromUploadFile(file))
            .then(preview => preview)
            .catch(() => null)
        }

        const downloadFile = async (url, fileName) => {
          let downloadUrl = url

          // Browsers may ignore `download` for cross-origin URLs, so convert to blob URL first.
          if (/^https?:\/\//i.test(url) && !url.startsWith(window.location.origin)) {
            const response = await fetch(url)
            if (!response.ok) {
              throw new Error(`Failed to fetch file: ${response.status}`)
            }

            const contentType = (response.headers.get('content-type') || '').toLowerCase().trim()
            if (contentType.includes('text/html') || contentType.includes('application/json')) {
              throw new Error(`Invalid file response content-type: ${contentType || 'unknown'}`)
            }

            const blob = await response.blob()
            downloadUrl = URL.createObjectURL(blob)
          }

          const link = document.createElement('a')
          link.href = downloadUrl
          link.download = fileName || 'download'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

          if (downloadUrl.startsWith('blob:')) {
            setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000)
          }
        }

        if (file.url) {
          await downloadFile(file.url, file.name || 'download')
        } else if (filePreview) {
          const base64Data = filePreview.split(',')[1]
          const byteCharacters = atob(base64Data)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], {
            type: file.type || 'application/octet-stream'
          })
          const url = URL.createObjectURL(blob)
          await downloadFile(url, file.name || 'download')
        } else {
          throw new Error('No file source available')
        }
      } catch (error) {
        this.showNotification('error', 'Download Error', 'Failed to download file. It may be unavailable or removed.')
      }
    },
    openPdfPreview(file) {
      const source = this.getFileSource(file)
      if (!source) {
        this.showNotification('error', 'Open Error', 'Failed to open file. It may be unavailable or removed.')

        return
      }

      this.pdfPreviewFile = source
      this.pdfPreviewVisible = true
    },
    closePdfPreview() {
      this.pdfPreviewVisible = false
      this.pdfPreviewFile = null
    }
  },
  mounted() {
    this.fileList = this.files
      .map(item => ({
        uid: item.id,
        name: item.url,
        url: item.url,
        status: 'done'
      }))
      .sort((a, b) => a.uid - b.uid)
  }
}
</script>

<style scoped>
.column {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(20px);
  opacity: 0;
}
</style>
